import ApplicationController from "./application_controller"

/*
 * Accessibility — large print (WCAG 1.4.4 Resize Text):
 * Root rem scaling via --lq-text-scale (lexiqamus.scss). Toolbar A−/A/A+ in
 * _accessibility_toolbar.html.erb may be disabled in ERB; restoreTextScale still
 * reapplies a saved multiplier from localStorage on public LexiQamus layouts.
 */
const LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY = "lexiqamusTextScale"
const LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS = [0.875, 1, 1.125, 1.25, 1.5]
const LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER = 1

/*
 * Accessibility — screen magnifier (pointer magnifying lens):
 * Floating toolbar toggle clones .lexiqamus-wrapper (nav, header, main, footer)
 * into a circular lens that follows the pointer. Lens and mirror use
 * pointer-events: none so clicks/scroll hit the real page
 * (_accessibility_toolbar.html.erb + lexiqamus.scss). Search home may render only
 * the toolbar aside inside .lexiqamus-wrapper for tab order; the lens/mirror stay
 * outside the wrapper. buildLensMirrorClone strips any stray toolbar/lens nodes.
 */
const MAGNIFIER_SCALE = 2
// Keep in sync with $lexiqamus-magnifier-lens-size in lexiqamus.scss.
const MAGNIFIER_LENS_SIZE_PX = 180
const MAGNIFIER_SOURCE_SELECTOR = ".lexiqamus-wrapper"
const MAGNIFIER_MIRROR_SYNC_SELECTOR = "#lexiqamus-innovations-root"
const MAGNIFIER_MIRROR_SEARCH_SYNC_SELECTOR = ".js-lexiqamus-search-results-root"
const MAGNIFIER_CLONE_FIXED_ELEMENT_SELECTORS = [
  "#resultsFixedStickyNav",
  ".results-column-header-sticky.is-attached",
]
const MAGNIFIER_MIRROR_NO_INNER_HTML_SYNC_IDS = [
  "lexiqamus-innovations-root",
  "sectionFlow",
  "lexiqamusFlowStage",
]
// Max direct element children for wholesale innerHTML sync (typewriter nodes, not whole sections).
const MAGNIFIER_MIRROR_MAX_INNER_HTML_SYNC_CHILD_COUNT = 15

function resolveMagnifierMirrorSyncRoot(source) {
  return (
    source.querySelector(MAGNIFIER_MIRROR_SYNC_SELECTOR) ||
    source.querySelector(MAGNIFIER_MIRROR_SEARCH_SYNC_SELECTOR)
  )
}

function magnifierCloneSyncTarget(clone, syncRoot) {
  const id = syncRoot.getAttribute("id")
  if (id) return clone.querySelector(`#${CSS.escape(id)}`)
  return clone.querySelector(MAGNIFIER_MIRROR_SEARCH_SYNC_SELECTOR)
}

function setMagnifierCloneLayout(source, clone) {
  clone.style.width = `${source.offsetWidth}px`
  clone.style.minHeight = `${Math.max(source.offsetHeight, source.scrollHeight)}px`
}

// Viewport-fixed search chrome misaligns inside the scaled lens clone — map to wrapper-relative absolute.
function normalizeMagnifierCloneFixedElements(source, clone) {
  const sourceRect = source.getBoundingClientRect()
  MAGNIFIER_CLONE_FIXED_ELEMENT_SELECTORS.forEach((selector) => {
    source.querySelectorAll(selector).forEach((liveEl) => {
      if (getComputedStyle(liveEl).position !== "fixed") return
      const liveId = liveEl.getAttribute("id")
      const cloneEl = liveId
        ? clone.querySelector(`#${CSS.escape(liveId)}`)
        : clone.querySelector(selector)
      if (!cloneEl) return

      const rect = liveEl.getBoundingClientRect()
      cloneEl.style.position = "absolute"
      cloneEl.style.top = `${rect.top - sourceRect.top}px`
      cloneEl.style.left = `${rect.left - sourceRect.left}px`
      cloneEl.style.width = `${rect.width}px`
      cloneEl.style.height = `${rect.height}px`
      const transform = getComputedStyle(liveEl).transform
      cloneEl.style.transform = transform !== "none" ? transform : ""
    })
  })
}

// Snap stored/custom values to a toolbar step so UI state and CSS stay in sync.
function nearestAllowedTextScale(value) {
  const parsedTextScale = parseFloat(value)
  if (!Number.isFinite(parsedTextScale)) return LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
  return LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.find(
    (multiplier) => Math.abs(multiplier - parsedTextScale) < 0.001
  ) || LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
}

// Body data-controller on lexiqamus_public_page + lexiqamus_results layouts.
export default class extends ApplicationController {
  static targets = [
    // TODO(CPG-381): Text-size buttons are commented out in _accessibility_toolbar.html.erb (<% if false %>).
    // Keep targets so restoreTextScale / setLargePrintTextScale stay wired when the group is re-enabled.
    "decreaseTextButton",
    "resetTextButton",
    "increaseTextButton",
    "magnifierToggle",
    "magnifierLens",
    "magnifierMirror",
  ]

  connect() {
    this.textScale = LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
    this.magnifierActive = false
    this.lastClientX = 0
    this.lastClientY = 0
    this.magnifierSourceElement = null
    this.magnifierMirrorSyncRoot = null
    this.restoreTextScale()
  }

  disconnect() {
    this.disableMagnifier()
  }

  // Reapply saved scale on load without firing resize (layout is not dependent on scale yet).
  restoreTextScale() {
    let scale = LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
    try {
      scale = nearestAllowedTextScale(window.localStorage.getItem(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY))
    } catch (_e) {
      // ignore localStorage errors (private browsing, quota exceeded)
    }
    this.setLargePrintTextScale(scale, scale !== LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER)
  }

  // Step down one entry in LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS (no-op at minimum).
  decreaseTextSize(event) {
    const currentMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.indexOf(this.textScale)
    if (currentMultiplierIndex > 0) {
      this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[currentMultiplierIndex - 1])
      this.announceFromControl(event?.currentTarget)
    }
  }

  // Step up one entry; indexOf guard covers a scale value that is not an exact step after restore.
  increaseTextSize(event) {
    const currentMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.indexOf(this.textScale)
    const lastMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.length - 1
    if (currentMultiplierIndex >= 0 && currentMultiplierIndex < lastMultiplierIndex) {
      this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[currentMultiplierIndex + 1])
      this.announceFromControl(event?.currentTarget)
    }
  }

  resetTextSize(event) {
    this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER)
    this.announceFromControl(event?.currentTarget)
  }

  // Single write path: CSS var drives rem scaling; persist choice; sync toolbar disabled/pressed state.
  setLargePrintTextScale(scale, notifyLayout = true) {
    this.textScale = nearestAllowedTextScale(scale)
    document.documentElement.style.setProperty("--lq-text-scale", String(this.textScale))
    this.updateTextSizeToolbarButtonStates()

    // Best-effort persistence — scaling still works if storage is unavailable.
    try {
      window.localStorage.setItem(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY, String(this.textScale))
    } catch (_e) {
      // ignore localStorage errors (private browsing, quota exceeded)
    }
    // TOC / scroll-spy and other width listeners recalc after rem scale changes.
    if (notifyLayout) window.dispatchEvent(new Event("resize"))
  }

  /*
   * Accessibility — magnifier toggle (toolbar 🔍):
   * Off always runs disable + “disabled” announcement. On only when enableMagnifier succeeds
   * (lens/mirror targets present and buildLensMirrorClone OK); otherwise aria-pressed stays false
   * and no “enabled” announcement (WCAG 4.1.3 — no false success).
   */
  toggleMagnifier(event) {
    if (this.magnifierActive) {
      this.disableMagnifier()
      this.announceMagnifierToggle(event?.currentTarget, false)
    } else if (this.enableMagnifier(event)) {
      this.announceMagnifierToggle(event?.currentTarget, true)
    }
  }

  // Show lens, bind passive document listeners, sync toggle state; never preventDefault on pointer events.
  enableMagnifier(event) {
    if (!this.hasMagnifierLensTarget || !this.hasMagnifierMirrorTarget) return false
    if (!this.buildLensMirrorClone()) return false

    const isPointerClick = Boolean(event?.clientX || event?.clientY)
    this.lastClientX = isPointerClick ? event.clientX : window.innerWidth / 2
    this.lastClientY = isPointerClick ? event.clientY : window.innerHeight / 2
    this.magnifierActive = true

    document.body.classList.add("lexiqamus-magnifier-active")
    this.magnifierLensTarget.hidden = false
    if (this.hasMagnifierToggleTarget) {
      this.magnifierToggleTarget.setAttribute("aria-pressed", "true")
    }

    this.boundHandleMagnifierPointerMove = this.handleMagnifierPointerMove.bind(this)
    this.boundHandleMagnifierScroll = this.handleMagnifierScroll.bind(this)
    this.boundHandleMagnifierKeydown = this.handleMagnifierKeydown.bind(this)
    this.boundHandleMagnifierWindowResize = this.handleMagnifierWindowResize.bind(this)

    document.addEventListener("mousemove", this.boundHandleMagnifierPointerMove, { passive: true })
    document.addEventListener("scroll", this.boundHandleMagnifierScroll, { passive: true, capture: true })
    document.addEventListener("keydown", this.boundHandleMagnifierKeydown)
    window.addEventListener("resize", this.boundHandleMagnifierWindowResize, { passive: true })
    this.positionMagnifierAt(this.lastClientX, this.lastClientY)
    return true
  }

  /*
   * Tear down lens listeners and mirror DOM. No-op when already off (disconnect may call twice).
   * Cancel pending mirror rAF before clearing the clone so a stale frame cannot touch removed nodes.
   */
  disableMagnifier() {
    if (!this.magnifierActive) return

    if (this.magnifierMirrorSyncFrame != null) {
      cancelAnimationFrame(this.magnifierMirrorSyncFrame)
      this.magnifierMirrorSyncFrame = null
    }

    this.magnifierActive = false
    document.body.classList.remove("lexiqamus-magnifier-active")

    if (this.hasMagnifierLensTarget) this.magnifierLensTarget.hidden = true
    if (this.hasMagnifierMirrorTarget) this.magnifierMirrorTarget.innerHTML = ""
    this.magnifierSourceElement = null
    this.magnifierMirrorSyncRoot = null
    if (this.hasMagnifierToggleTarget) {
      this.magnifierToggleTarget.setAttribute("aria-pressed", "false")
    }

    document.removeEventListener("mousemove", this.boundHandleMagnifierPointerMove)
    document.removeEventListener("scroll", this.boundHandleMagnifierScroll, { capture: true })
    document.removeEventListener("keydown", this.boundHandleMagnifierKeydown)
    window.removeEventListener("resize", this.boundHandleMagnifierWindowResize)
  }

  /*
   * Deep-clone .lexiqamus-wrapper into the lens mirror. IDs stay for ID-scoped CSS on search home;
   * inert + aria-hidden keep duplicate IDs out of AT/Tab. ponytail: mirror lags AJAX until toggle/resize.
   */
  buildLensMirrorClone() {
    const source = document.querySelector(MAGNIFIER_SOURCE_SELECTOR)
    if (!source || !this.hasMagnifierMirrorTarget) return false

    const clone = source.cloneNode(true)
    clone.querySelector(".lexiqamus-accessibility-toolbar")?.remove()
    clone.querySelector(".lexiqamus-magnifier-lens")?.remove()
    clone.classList.add("lexiqamus-magnifier-clone")
    clone.setAttribute("aria-hidden", "true")
    clone.setAttribute("inert", "")
    setMagnifierCloneLayout(source, clone)
    clone.style.transform = `scale(${MAGNIFIER_SCALE})`

    this.magnifierSourceElement = source
    this.magnifierMirrorSyncRoot = resolveMagnifierMirrorSyncRoot(source)
    this.magnifierMirrorTarget.innerHTML = ""
    this.magnifierMirrorTarget.appendChild(clone)
    return true
  }

  /*
   * Innovations landing (#lexiqamus-innovations-root) or search results (.js-lexiqamus-search-results-root):
   * coalesce mirror updates to one rAF per pointer frame. Static CMS pages skip sync when no root exists.
   */
  scheduleMagnifierMirrorSync() {
    if (!this.magnifierMirrorSyncRoot) return
    if (this.magnifierMirrorSyncFrame != null) return
    this.magnifierMirrorSyncFrame = requestAnimationFrame(() => {
      this.magnifierMirrorSyncFrame = null
      if (this.magnifierActive) this.refreshLensMirrorFromLiveDom()
    })
  }

  /*
   * Refresh the static clone from the live wrapper: layout size, synced subtree
   * (classes, inline styles, typewriter/SVG markup), search fixed chrome, then
   * innovations canvas pixels (cloneNode does not copy bitmap data).
   */
  refreshLensMirrorFromLiveDom() {
    const source = this.magnifierSourceElement
    const syncRoot = this.magnifierMirrorSyncRoot
    const clone = this.magnifierMirrorTarget?.querySelector(".lexiqamus-magnifier-clone")
    if (!source || !syncRoot || !clone) return

    const syncClone = magnifierCloneSyncTarget(clone, syncRoot)
    if (!syncClone) return

    setMagnifierCloneLayout(source, clone)
    this.syncLensMirrorSubtree(syncRoot, syncClone)
    if (!syncRoot.getAttribute("id")) normalizeMagnifierCloneFixedElements(source, clone)

    if (syncRoot.getAttribute("id") === "lexiqamus-innovations-root") {
      const sourceCanvases = source.querySelectorAll("canvas")
      const cloneCanvases = clone.querySelectorAll("canvas")
      sourceCanvases.forEach((sourceCanvas, index) => {
        const cloneCanvas = cloneCanvases[index]
        if (!cloneCanvas) return
        try {
          if (sourceCanvas.width <= 0 || sourceCanvas.height <= 0) return
          cloneCanvas.width = sourceCanvas.width
          cloneCanvas.height = sourceCanvas.height
          cloneCanvas.getContext("2d")?.drawImage(sourceCanvas, 0, 0)
        } catch (_e) {
          // zero-size or tainted canvas
        }
      })
    }
  }

  /*
   * Walk parallel source/clone trees under innovations (same structure after cloneNode).
   * Sync animation state: start-anim, card positions, clip-path, etc. via class/style;
   * use getAttribute("id") — SVG .id can be SVGAnimatedString and breaks string checks.
   */
  syncLensMirrorSubtree(sourceNode, cloneNode) {
    if (sourceNode.nodeType !== Node.ELEMENT_NODE || cloneNode.nodeType !== Node.ELEMENT_NODE) return
    if (sourceNode.tagName !== cloneNode.tagName) return

    const sourceClass = typeof sourceNode.className === "string" ? sourceNode.className : sourceNode.getAttribute("class") || ""
    if (sourceClass !== (cloneNode.getAttribute("class") || "")) cloneNode.setAttribute("class", sourceClass)

    const sourceStyle = sourceNode.getAttribute("style")
    const cloneStyle = cloneNode.getAttribute("style")
    if (sourceStyle !== cloneStyle) {
      if (sourceStyle) cloneNode.setAttribute("style", sourceStyle)
      else cloneNode.removeAttribute("style")
    }

    if (sourceNode.hidden !== cloneNode.hidden) cloneNode.hidden = sourceNode.hidden

    const elementId = sourceNode.getAttribute("id") || ""
    const tag = sourceNode.tagName
    // Small lexiqamus* nodes (typewriter text) and SVG (#lexiqamusPyramidSvg paths): copy markup once.
    // Skip large section roots — wholesale innerHTML is slow and breaks layout in the lens.
    const syncMarkup =
      tag.toUpperCase() === "SVG" ||
      (elementId.startsWith("lexiqamus") &&
        !MAGNIFIER_MIRROR_NO_INNER_HTML_SYNC_IDS.includes(elementId) &&
        sourceNode.childElementCount <= MAGNIFIER_MIRROR_MAX_INNER_HTML_SYNC_CHILD_COUNT &&
        sourceNode.innerHTML !== cloneNode.innerHTML)
    if (syncMarkup) {
      cloneNode.innerHTML = sourceNode.innerHTML
      return
    }

    const childCount = Math.min(sourceNode.children.length, cloneNode.children.length)
    for (let i = 0; i < childCount; i++) {
      this.syncLensMirrorSubtree(sourceNode.children[i], cloneNode.children[i])
    }
  }

  // Document mousemove while magnifier is active — updates lens position and schedules mirror refresh.
  handleMagnifierPointerMove(event) {
    this.lastClientX = event.clientX
    this.lastClientY = event.clientY
    this.positionMagnifierAt(this.lastClientX, this.lastClientY)
  }

  // Recalc clone offset when the page scrolls but the pointer stays still (capture listener on document).
  handleMagnifierScroll() {
    this.positionMagnifierAt(this.lastClientX, this.lastClientY)
  }

  // Rebuild clone on viewport resize (layout width/height of .lexiqamus-wrapper may change).
  handleMagnifierWindowResize() {
    if (!this.buildLensMirrorClone()) return
    this.positionMagnifierAt(this.lastClientX, this.lastClientY)
  }

  // WCAG 2.1.1 — Escape exits magnifier without trapping focus in the lens (decorative, aria-hidden).
  handleMagnifierKeydown(event) {
    if (event.key !== "Escape") return
    this.disableMagnifier()
    if (this.hasMagnifierToggleTarget) {
      this.announceMagnifierToggle(this.magnifierToggleTarget, false)
    }
  }

  // Fixed lens follows viewport coords; clone uses getBoundingClientRect-relative local coords.
  positionMagnifierAt(clientX, clientY) {
    if (!this.magnifierActive || !this.hasMagnifierLensTarget || !this.hasMagnifierMirrorTarget) return

    const source = this.magnifierSourceElement
    if (!source) return

    const sourceRect = source.getBoundingClientRect()
    const localX = clientX - sourceRect.left
    const localY = clientY - sourceRect.top
    const radius = MAGNIFIER_LENS_SIZE_PX / 2

    // Lens center tracks the pointer exactly; cursor is hidden via CSS while active.
    this.magnifierLensTarget.style.left = `${clientX - radius}px`
    this.magnifierLensTarget.style.top = `${clientY - radius}px`

    const clone = this.magnifierMirrorTarget.querySelector(".lexiqamus-magnifier-clone")
    if (!clone) return

    clone.style.left = `${radius - localX * MAGNIFIER_SCALE}px`
    clone.style.top = `${radius - localY * MAGNIFIER_SCALE}px`
    this.scheduleMagnifierMirrorSync()
  }

  // WCAG 4.1.3 — magnifier on/off from data-lexiqamus-accessibility-announce-enabled/disabled on the toggle.
  announceMagnifierToggle(control, enabled) {
    const message = enabled
      ? control?.dataset?.lexiqamusAccessibilityAnnounceEnabled
      : control?.dataset?.lexiqamusAccessibilityAnnounceDisabled
    window.announceLexiqamusStatus?.(message)
  }

  // WCAG 4.1.3 — copy from data-lexiqamus-accessibility-announce on the clicked control (toolbar, sign-in, etc.).
  announceFromControl(control) {
    window.announceLexiqamusStatus?.(control?.dataset?.lexiqamusAccessibilityAnnounce)
  }

  // Disable A− / A+ at the ends of the scale so keyboard users do not activate no-op clicks.
  updateTextSizeToolbarButtonStates() {
    if (this.hasDecreaseTextButtonTarget) {
      this.decreaseTextButtonTarget.disabled = this.textScale <= LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[0]
    }
    if (this.hasIncreaseTextButtonTarget) {
      const largestTextScaleMultiplier = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[
        LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.length - 1
      ]
      this.increaseTextButtonTarget.disabled = this.textScale >= largestTextScaleMultiplier
    }
    if (this.hasResetTextButtonTarget) {
      // Reset is “selected” only at the default step (toggle-style control, not a separate mode).
      this.resetTextButtonTarget.setAttribute(
        "aria-pressed",
        this.textScale === LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER ? "true" : "false"
      )
    }
  }
}

/* Accessibility: screen reader live region in LexiQamus layouts (#lexiqamus-status-announcer).
 * Announces transient status messages (e.g. copied text, section toggles, text scale, magnifier). */
window.announceLexiqamusStatus = function(message) {
  if (!message) return
  const announcer = document.getElementById("lexiqamus-status-announcer")
  if (!announcer) return
  announcer.textContent = ""
  requestAnimationFrame(() => { announcer.textContent = message })
}

